import { useEffect, useState } from "react";

/**
 * Renders sanitized HTML without shipping DOMPurify in the initial JS bundle.
 * On SSR / first paint we emit a plain-text fallback stripped of tags
 * (safe — never inserted as HTML). Once mounted, we dynamically import
 * DOMPurify and swap in the fully-styled sanitized markup.
 *
 * This keeps ~40 kB (min+gz) off the LCP bundle for product pages.
 */
export function SafeHtml({ html, className }: { html: string; className?: string }) {
  const [safe, setSafe] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    import("dompurify").then((mod) => {
      if (!alive) return;
      const DOMPurify = mod.default ?? mod;
      setSafe(
        DOMPurify.sanitize(html, {
          ALLOWED_TAGS: ["p","br","strong","em","b","i","u","a","ul","ol","li","h2","h3","h4","span","div","img"],
          ALLOWED_ATTR: ["href","title","target","rel","src","alt","width","height"],
          ALLOW_DATA_ATTR: false,
        }),
      );
    });
    return () => {
      alive = false;
    };
  }, [html]);

  if (safe !== null) {
    return <div className={className} dangerouslySetInnerHTML={{ __html: safe }} />;
  }

  // First paint: text-only fallback (no HTML injection possible).
  const plain = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return <div className={className}>{plain}</div>;
}